import type { StockBalance, WarehouseLocation } from '../../domain/fulfillment';
import type { SearchableSelectOption } from '../../components/SearchableSelect';

export interface LocationStock {
  locationId: string;
  available: number;
}

/**
 * Where a stock item is available, most first: active locations with any
 * of it not already reserved.
 */
export function stockByLocation(
  balances: readonly StockBalance[],
  locations: readonly WarehouseLocation[],
  stockItemId: string,
): LocationStock[] {
  const active = new Set(locations.filter((l) => l.active).map((l) => l.id));
  return balances
    .filter((b) => b.stockItemId === stockItemId && active.has(b.locationId) && b.available > 0)
    .map((b) => ({ locationId: b.locationId, available: b.available }))
    .sort((a, b) => b.available - a.available || a.locationId.localeCompare(b.locationId));
}

/**
 * The "Pick from" choices for a stock line: only the
 * locations that have the item, each with how much is available, so a
 * near-duplicate empty bin can't be chosen by mistake. A location the line
 * already names stays listed even when it has none. Until the balances
 * load (or before an item is chosen) every active location is listed.
 */
export function pickFromOptions({
  locations,
  balances,
  stockItemId,
  current,
  locationName,
}: {
  locations: readonly WarehouseLocation[];
  balances: readonly StockBalance[] | undefined;
  stockItemId: string | undefined;
  current: string | undefined;
  locationName: (id: string) => string;
}): SearchableSelectOption[] {
  const release: SearchableSelectOption = { value: '', label: 'Choose at release' };
  const byId = new Map(locations.map((l) => [l.id, l]));
  const option = (id: string, available?: number): SearchableSelectOption => {
    const code = byId.get(id)?.code;
    return {
      value: id,
      label: locationName(id),
      ...(code ? { aliases: [code] } : {}),
      ...(available === undefined ? {} : { count: available, countLabel: 'available' }),
    };
  };
  if (!balances || !stockItemId) {
    return [release, ...locations.filter((l) => l.active).map((l) => option(l.id))];
  }
  const stocked = stockByLocation(balances, locations, stockItemId);
  const options = stocked.map((s) => option(s.locationId, s.available));
  if (current && !stocked.some((s) => s.locationId === current)) {
    options.push(option(current, 0));
  }
  return [release, ...options];
}

/**
 * Why a line's chosen location can't cover it, e.g. "BOX 15 ETHERNET has 0
 * available; BOX 15M ETHERNET has 45", and that other location if any. Release reserves only from the
 * chosen location, so the line would otherwise wait for stock that is
 * already on a shelf elsewhere. `quantity` is what the line still needs
 * at its location; `moveQuantity`, if more, is what another location must
 * have to take the whole line over (a line is never split). Undefined when
 * the location covers it, or when there is nothing to check.
 */
export function locationShortfall({
  locations,
  balances,
  stockItemId,
  locationId,
  quantity,
  moveQuantity = quantity,
  locationName,
}: {
  locations: readonly WarehouseLocation[];
  balances: readonly StockBalance[] | undefined;
  stockItemId: string | undefined;
  locationId: string | undefined;
  quantity: number;
  moveQuantity?: number;
  locationName: (id: string) => string;
}): { text: string; alternative?: LocationStock } | undefined {
  if (!balances || !stockItemId || !locationId || !Number.isFinite(quantity) || quantity <= 0) {
    return undefined;
  }
  const stocked = stockByLocation(balances, locations, stockItemId);
  const here = stocked.find((s) => s.locationId === locationId)?.available ?? 0;
  if (here >= quantity) return undefined;
  const elsewhere = stocked.filter(
    (s) => s.locationId !== locationId && s.available >= moveQuantity,
  );
  const best = elsewhere[0];
  const alternative = best
    ? `; ${locationName(best.locationId)} has ${best.available}`
    : stocked.some((s) => s.locationId !== locationId)
      ? '; no other location has enough either'
      : '';
  const text = `${locationName(locationId)} has ${here} available${alternative}`;
  return best ? { text, alternative: best } : { text };
}
